import { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useConsent } from '@src/corelib/consent';

import ConsentPreferences from './ConsentPreferences';

export const OPEN_PREFERENCES_EVENT = 'sol:open-consent-preferences';

/** Lets anything on the page (e.g. the footer link) reopen the dialog. */
export function openConsentPreferences() {
  window.dispatchEvent(new CustomEvent(OPEN_PREFERENCES_EVENT));
}

function ConsentBanner() {
  const { hasDecided, acceptAll, rejectAll } = useConsent();
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  useEffect(() => {
    const open = () => setPreferencesOpen(true);
    window.addEventListener(OPEN_PREFERENCES_EVENT, open);
    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, open);
  }, []);

  return (
    <>
      {!hasDecided && (
        <Paper
          elevation={8}
          role="dialog"
          aria-label="Cookie consent"
          data-testid="consent-banner"
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1400,
            p: 2,
            borderRadius: 0,
          }}
        >
          <Stack
            spacing={2}
            justifyContent="space-between"
            sx={{ maxWidth: 1200, mx: 'auto' }}
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'stretch', md: 'center' }}
          >
            <Typography variant="body2">
              We use strictly necessary cookies to run this site. With your permission we would also
              use analytics and marketing cookies, including session recording.
              {' '}
              <Link href="/cookie-policy">Cookie Policy</Link>
            </Typography>

            {/*
              "Accept all" and "Reject all" carry identical visual weight and sit
              at the same level. Making rejection harder than acceptance is what
              CNIL fined Google and Meta for — this is a legal constraint, not a
              style choice. Do not demote "Reject all" to a text link.
            */}
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              <Button variant="outlined" data-testid="consent-preferences" onClick={() => setPreferencesOpen(true)}>
                Preferences
              </Button>

              <Button variant="contained" data-testid="consent-reject-all" onClick={rejectAll}>
                Reject all
              </Button>

              <Button variant="contained" data-testid="consent-accept-all" onClick={acceptAll}>
                Accept all
              </Button>
            </Box>
          </Stack>
        </Paper>
      )}

      <ConsentPreferences open={preferencesOpen} onClose={() => setPreferencesOpen(false)} />
    </>
  );
}

export default ConsentBanner;
